import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Tournament {
  id: string;
  fiscal_year_id: string;
  name: string;
  slug: string;
  tournament_goal: number | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  fundraising_goal: number;
  contingency_percentage: number;
  baseball_cost_per_dozen: number;
  balls_per_game: number;
  transaction_fee_percentage: number;
  software_fee_per_team: number;
  scheduling_fee_per_game: number;
  pool_play_games: number;
  awards_cost_per_item: number;
  awards_items_per_team: number;
  registration_fee_per_team: number;
  discount_amount_per_team: number;
  deposit_amount_per_team: number;
  use_bracket: boolean;
  branding_logo_url: string | null;
  branding_primary_color: string | null;
  branding_accent_color: string | null;
  branding_tagline: string | null;
  is_public: boolean;
}

interface FiscalYear {
  id: string;
  year: number;
  name: string;
  fundraising_goal: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

interface TournamentContextType {
  activeTournament: Tournament | null;
  activeFiscalYear: FiscalYear | null;
  allTournaments: Tournament[];
  loading: boolean;
  switchTournament: (tournamentId: string) => Promise<void>;
  switchTournamentBySlug: (slug: string) => Promise<void>;
  getTournamentById: (tournamentId: string) => Tournament | null;
  refreshTournaments: () => Promise<void>;
}

const TournamentContext = createContext<TournamentContextType | undefined>(undefined);

export function TournamentProvider({ children }: { children: ReactNode }) {
  const [activeTournament, setActiveTournament] = useState<Tournament | null>(null);
  const [activeFiscalYear, setActiveFiscalYear] = useState<FiscalYear | null>(null);
  const [allTournaments, setAllTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTournaments = async () => {
    try {
      setLoading(true);

      // Fetch all tournaments
      const { data: tournaments, error: tournamentsError } = await supabase
        .from('tournaments')
        .select('*')
        .order('created_at', { ascending: false });

      if (tournamentsError) throw tournamentsError;

      setAllTournaments(tournaments || []);

      // Get active tournament or the first one
      const active = tournaments?.find(t => t.is_active) || tournaments?.[0];
      setActiveTournament(active || null);

      // Fetch active fiscal year if we have an active tournament
      if (active?.fiscal_year_id) {
        const { data: fiscalYear, error: fiscalYearError } = await supabase
          .from('fiscal_years')
          .select('*')
          .eq('id', active.fiscal_year_id)
          .single();

        if (fiscalYearError) throw fiscalYearError;
        setActiveFiscalYear(fiscalYear);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchTournament = async (tournamentId: string) => {
    try {
      // Update the previous active tournament
      if (activeTournament) {
        await supabase
          .from('tournaments')
          .update({ is_active: false })
          .eq('id', activeTournament.id);
      }

      // Update the new active tournament
      await supabase
        .from('tournaments')
        .update({ is_active: true })
        .eq('id', tournamentId);

      // Refresh tournaments
      await fetchTournaments();
    } catch (error) {
      console.error('Error switching tournament:', error);
      throw error;
    }
  };

  const switchTournamentBySlug = async (slug: string) => {
    try {
      const tournament = allTournaments.find(t => t.slug === slug);
      if (!tournament) {
        console.error('Tournament not found with slug:', slug);
        return;
      }
      await switchTournament(tournament.id);
    } catch (error) {
      console.error('Error switching tournament by slug:', error);
      throw error;
    }
  };

  const getTournamentById = (tournamentId: string): Tournament | null => {
    return allTournaments.find(t => t.id === tournamentId) || null;
  };

  useEffect(() => {
    fetchTournaments();

    // Set up real-time subscription for tournament changes
    const channel = supabase
      .channel('tournament-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournaments'
        },
        () => {
          fetchTournaments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <TournamentContext.Provider
      value={{
        activeTournament,
        activeFiscalYear,
        allTournaments,
        loading,
        switchTournament,
        switchTournamentBySlug,
        getTournamentById,
        refreshTournaments: fetchTournaments,
      }}
    >
      {children}
    </TournamentContext.Provider>
  );
}

export function useTournament() {
  const context = useContext(TournamentContext);
  if (context === undefined) {
    throw new Error('useTournament must be used within a TournamentProvider');
  }
  return context;
}
